import React, { useContext, useMemo, useCallback, useState } from 'react';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import ProductsScreen from '../../screens/products/ProductsScreen';
import Ionicons from '@react-native-vector-icons/ionicons/static';
import Foundation from '@react-native-vector-icons/foundation/static';
import SimpleLineIcons from '@react-native-vector-icons/simple-line-icons/static';
import MaterialIcons from '@react-native-vector-icons/material-icons/static';
import MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import HeaderLeft from '../../components/Reusables/headers/HeaderLeft';
import HeaderActions from '../../components/Reusables/headers/HeaderActions';
import HeaderIconButton from '../../components/Reusables/headers/HeaderIconButton';
import HeaderInternetStatusIcon from '../../components/Reusables/headers/HeaderInternetStatusIcon';
import HeaderLocationPicker from '../../components/Reusables/headers/HeaderLocationPicker';
import BackOfficeHeaderLocationPicker from '../../components/Reusables/headers/BackOfficeHeaderLocationPicker';
import AppSearchBar from '../../components/Reusables/AppSearchBar';
import AppSearchBarWeb from '../../components/Reusables/AppSearchBar.web';
import OrdersScreenTabNavigator from './OrdersScreenTabNavigator';
import { StyleSheet, View, Platform } from 'react-native';
import { BlurView } from '@react-native-community/blur';
import { RouteProp } from '@react-navigation/native';
import { isEmployeeAdminRole, isEmployeeOwnerRole } from '../../utils/roles';
import Typography from '../../components/Reusables/Typography';
import SearchContext from '../../Context/SearchContext';
import { useDispatch } from 'react-redux';
import {
  clearFilteredCompletedOrder,
  clearFilteredPartialOrder,
  clearFilteredPendingOrder,
} from '../../redux/OrdersSlice';
import moment from 'moment';
import Cart from '../../screens/CartScreen';
import CartIcon from '../../components/Reusables/CartIcon';
import { ModalContext } from '../../Context/modalContext';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import {
  useTheme,
  useThemeColors,
  GTFonts,
  palette,
  useLayoutSize,
} from '../../theme';
import { isTablet } from 'react-native-device-info';
import { fs, s, v, iconSize, borderWidth } from '../../utils/libs';
import { getApiMessage } from '../../utils/apiError';
import { useAppSelector } from '../../redux/helper';
import {
  addSingleSetting,
  selectProductCard,
  selectProductCardLayout,
} from '../../redux/SettingsSlice';
import useBackofficeOwnerAuth from '../../hooks/useBackofficeOwnerAuth';
import useShowServiceDisplay from '../../hooks/useShowServiceDisplay';
import ErrorContext from '../../Context/ErrorContext';
import BackOfficeSummaryScreen from '../../screens/backoffice/BackOfficeSummaryScreen';
import { RootState } from '../../redux/types';
import { Menu } from 'react-native-paper';
import CalculatorModal from '../../components/Modals/CalculatorModal';
import WebSidebarLayout from '../../components/web/WebSidebarLayout';
import WebHeaderBarInjector from '../../components/web/WebHeaderBarInjector';
import { useSecureNavigate } from '../security/useSecureNavigate';
import EmployeeProfileScreen from '../../screens/employee/EmployeeProfileScreen';
import { shouldShowTabletSidebarLayoutSetting } from '../../utils/layoutUtils';
import DateRangeDropdown from '../../components/Reusables/headers/DateRangeDropdown';
import { setSummaryRange } from '../../redux/backoffice/summarySlice';
import ChatwootService from '../../services/ChatwootService';
import { requestNotificationsOnUserGesture } from '../../services/requestNotificationsOnUserGesture';
import useHeaderStatusAction from '../../hooks/useHeaderStatusAction';
type TabParamList = {
  StatsSummaryScreen: undefined;
  ProductsScreen: { refreshFunction: () => void };
  Cart: undefined;
  Orders: undefined;
  Office: undefined;
  Profile: undefined;
  Home: undefined;
  MainScreen: { screen: string };
};

type MainScreenTabNavigatorProps = {
  navigation: NativeStackNavigationProp<any>;
  route: RouteProp<TabParamList, 'ProductsScreen'>;
};

const Tab = createBottomTabNavigator<TabParamList>();

// Soft rounded pill highlight behind the active tab icon (Concept 1).
// Absolutely positioned so it adds no layout height (never clips labels).
const TabIconPill: React.FC<{
  focused: boolean;
  children: React.ReactNode;
}> = ({ focused, children }) => (
  <View style={styles.tabIconWrap}>
    {focused && <View pointerEvents="none" style={styles.tabIconPill} />}
    {children}
  </View>
);

const MainScreenTabNavigator: React.FC<MainScreenTabNavigatorProps> = ({
  navigation,
  route,
}) => {
  const { theme, isDark } = useTheme();
  const { width: deviceWidth, height: deviceHeight } = useLayoutSize();
  const showTabletSidebarLayoutSetting = shouldShowTabletSidebarLayoutSetting(
    deviceWidth,
    deviceHeight,
  );
  const colors = useThemeColors();
  const { orderSearchId, setOrderSearchId } = useContext(SearchContext);
  const { setFilterModal } = useContext(ModalContext);
  // const [touch] = useState<boolean>(false);
  const productCardLayout = useAppSelector(selectProductCardLayout);
  const productCard = useAppSelector(selectProductCard);
  const refreshFunction = route.params?.refreshFunction;
  const dispatch = useDispatch();
  const { autoLoginOwner } = useBackofficeOwnerAuth();
  const { secureNavigate } = useSecureNavigate(navigation as any);
  const { handleError } = useContext(ErrorContext);
  const employeeRole = useAppSelector(
    (state: RootState) =>
      state.auth.inApp.account.employeeInfo?.bp_employee_role,
  );
  const isAdmin = isEmployeeAdminRole(employeeRole);
  const isOwner = isEmployeeOwnerRole(employeeRole);
  const { showservicedisplay } = useShowServiceDisplay();
  const selectedSummaryRange = useAppSelector(
    s => (s as any).backofficeSummary.selectedRange,
  );
  const [menuVisibleCart, setMenuVisibleCart] = useState(false);
  const [calcOpen, setCalcOpen] = useState<boolean>(false);
  const [ordersDateFilterVisible, setOrdersDateFilterVisible] =
    useState(false);
  const [ordersDateFilterActive, setOrdersDateFilterActive] = useState(false);
  const [ordersDateFilterStartISO, setOrdersDateFilterStartISO] = useState(
    moment().startOf('month').toISOString(),
  );
  const [ordersDateFilterEndISO, setOrdersDateFilterEndISO] = useState(
    moment().endOf('day').toISOString(),
  );
  const openMenuCart = useCallback(() => setMenuVisibleCart(true), []);
  const closeMenuCart = useCallback(() => setMenuVisibleCart(false), []);

  const handleToggleProductLayout = useCallback(() => {
    const next = productCardLayout === 'row' ? 'grid' : 'row';
    dispatch(
      addSingleSetting({
        productCard: { ...productCard, layout: next },
      }),
    );
  }, [dispatch, productCard, productCardLayout]);

  const handleOpenNotifications = useCallback(() => {
    void requestNotificationsOnUserGesture();
    (navigation as any).navigate('Notifications');
  }, [navigation]);

  // Products header status slot (mobile priority, utils/headerStatusSlot):
  //   default: chat for admin, layout toggle for non-admin
  const productsStatusAction = useHeaderStatusAction({
    onOpenNotifications: handleOpenNotifications,
    fallback: isAdmin
      ? {
          type: 'icon',
          icon: 'chatbubbles-sharp',
          iconLibrary: 'ionicons',
          onPress: () => void ChatwootService.openChat(),
          accessibilityLabel: 'Customer support',
        }
      : {
          type: 'icon',
          icon:
            productCardLayout === 'row' ? 'view-grid-outline' : 'view-list',
          onPress: handleToggleProductLayout,
          accessibilityLabel:
            productCardLayout === 'row'
              ? 'Switch to grid layout'
              : 'Switch to list layout',
        },
  });

  const { width, useSidebarNav: useSidebar } = useLayoutSize();
  const useNativeNav = !useSidebar;
  const isTabletCompact = width >= 768 && width < 1024;
  const webHeaderSearchMaxWidth = isTabletCompact ? s(660) : s(620);
  const nativeHeaderSearchWidth = isTablet()
    ? Math.max(width * 0.7, width - s(110))
    : width * 0.7;
  const handleClearSearch = () => {
    setOrderSearchId('');
    dispatch(clearFilteredCompletedOrder());
    dispatch(clearFilteredPendingOrder());
    dispatch(clearFilteredPartialOrder());
  };

  const handleToggleOrdersDateFilter = useCallback(() => {
    setOrdersDateFilterVisible(value => !value);
  }, []);

  const handleApplyOrdersDateFilter = useCallback(
    (fromISO: string, toISO: string) => {
      setOrderSearchId('');
      dispatch(clearFilteredCompletedOrder());
      dispatch(clearFilteredPendingOrder());
      dispatch(clearFilteredPartialOrder());
      setOrdersDateFilterStartISO(fromISO);
      setOrdersDateFilterEndISO(toISO);
      setOrdersDateFilterActive(true);
      setOrdersDateFilterVisible(false);
    },
    [dispatch, setOrderSearchId],
  );

  const handleBackOfficePress = useCallback(async () => {
    try {
      // navigation.navigate('BackOffice');
      await autoLoginOwner();
    } catch (e: any) {
      handleError(getApiMessage(e, 'Owner login error'), 'error', true);
    }
  }, [navigation, autoLoginOwner, handleError]);

  const handleOfficeTabPress = useCallback(async () => {
    try {
      void autoLoginOwner();
      await secureNavigate('BackOffice');
    } catch (e: any) {
      handleError(getApiMessage(e, 'Owner login error'), 'error', true);
    }
  }, [autoLoginOwner, secureNavigate, handleError]);

  // const handleCartPress = useCallback(() => {
  //   navigation.navigate('Cart');
  // }, [navigation]);

  // Enhanced tab bar styling with theme colors
  // Hide bottom tab bar when using sidebar nav (web large, native tablet landscape)
  const tabBarStyle = useMemo(() => {
    if (useSidebar) {
      return {
        display: 'none' as const,
      };
    }

    const basePaddingBottom = Platform.OS === 'ios' ? v(25) : v(8);
    const paddingTop = v(2);
    const contentSpace = v(56); // icon + label + margins; v(50) was clipping labels
    const minHeight = paddingTop + contentSpace + basePaddingBottom;

    return {
      backgroundColor: 'transparent',
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)',
      elevation: 0,
      shadowColor: 'transparent',
      minHeight,
      paddingBottom: basePaddingBottom,
      paddingTop: paddingTop,
    };
  }, [colors, isDark, theme, useSidebar]);

  const headerStyle = useMemo(
    () => ({
      backgroundColor: theme.colors.primaryDark, // Uses #1560BD - your brand header color
      ...theme.shadows.md,
    }),
    [colors, theme],
  );

  // Enhanced tab bar options with better visual feedback
  const screenOptions = useMemo(
    () => ({
      tabBarActiveTintColor: palette.blue[500], // #0088FE - shared with backoffice active
      tabBarInactiveTintColor: theme.colors.textGray500,
      tabBarBackground: () => (
        <View style={StyleSheet.absoluteFill}>
          {Platform.OS === 'ios' && (
            <BlurView
              style={StyleSheet.absoluteFill}
              blurType={isDark ? 'dark' : 'light'}
              blurAmount={20}
              reducedTransparencyFallbackColor={isDark ? '#101828' : '#ffffff'}
            />
          )}
          <View
            style={[
              StyleSheet.absoluteFill,
              {
                backgroundColor: isDark
                  ? Platform.OS === 'ios'
                    ? 'rgba(16,24,40,0.55)'
                    : 'rgba(16,24,40,0.92)'
                  : Platform.OS === 'ios'
                    ? 'rgba(255,255,255,0.65)'
                    : 'rgba(255,255,255,0.92)',
              },
            ]}
          />
        </View>
      ),
      tabBarLabelStyle: {
        fontFamily: GTFonts.regular,
        fontSize: fs(11),
        lineHeight: fs(18),
        fontWeight: '600' as const,
        textTransform: 'uppercase' as const,
        marginTop: v(4),
        letterSpacing: 0.5,
      },
      tabBarStyle,
      tabBarItemStyle: {
        paddingVertical: s(6),
        borderRadius: isTablet()
          ? theme.borderRadius.md
          : theme.borderRadius.sm,
        // marginHorizontal: s(4),
      },
      tabBarIconStyle: {
        marginBottom: v(2),
      },
      headerTitleAlign: 'left' as const,
    }),
    [colors, isDark, theme, tabBarStyle],
  );

  return (
    <>
      <WebSidebarLayout>
        <Tab.Navigator
          screenOptions={screenOptions}
          initialRouteName={showservicedisplay ? 'Profile' : 'ProductsScreen'}
          safeAreaInsets={{ bottom: 0 }}
          backBehavior="initialRoute">
        {isAdmin && (
          <Tab.Screen
            name="StatsSummaryScreen"
            options={{
              title: 'Stats',
              headerShown: useNativeNav,
              headerTitle: () => null,
              headerTitleStyle: {
                color: 'white',
              },
              headerShadowVisible: false,
              headerTintColor: '#fff',
              tabBarLabel: 'Stats',
              tabBarLabelStyle: {
                fontSize: fs(12),
                fontFamily: GTFonts.regular,
                lineHeight: fs(18),
              },
              tabBarIcon: ({ focused }) => (
                <TabIconPill focused={focused}>
                  <Foundation
                    name="graph-pie"
                    size={iconSize(isTablet() ? 18 : focused ? 30 : 28)}
                    color={focused ? palette.blue[500] : theme.colors.textGray500}
                  />
                </TabIconPill>
              ),
              headerStyle,
            }}
            listeners={{
              tabPress: e => {
                handleBackOfficePress();
              },
            }}>
            {(props: any) => (
              <>
                <WebHeaderBarInjector
                  title={<BackOfficeHeaderLocationPicker />}
                  onBack={() =>
                    navigation.popTo('DrawerNavigation', { screen: 'Home' })
                  }
                  rightSlot={
                    <DateRangeDropdown
                      variant="compact"
                      chipBgColor={'rgba(255,255,255,0.15)'}
                      chipTextColor={'#fff'}
                      chipBorderColor={'rgba(255,255,255,0.35)'}
                      selectedOverride={selectedSummaryRange}
                      onChange={value =>
                        dispatch(setSummaryRange(value as any))
                      }
                    />
                  }
                />
                <BackOfficeSummaryScreen {...props} />
              </>
            )}
          </Tab.Screen>
        )}
        {!showservicedisplay && (
          <Tab.Screen
            name="ProductsScreen"
            options={{
              title: 'Products',
              // Hide header on web - ProductList.web.tsx has its own header
              headerShown: useNativeNav,
              headerTitleContainerStyle: { flex: 1 },
              headerTitleStyle: {
                color: 'white',
              },
              headerShadowVisible: false,
              headerTintColor: '#fff',
              headerTitle: () => (
                <View
                  style={{
                    width: '100%',
                    maxWidth:
                      Platform.OS === 'web'
                        ? webHeaderSearchMaxWidth
                        : undefined,
                  }}>
                  <AppSearchBar
                    placeholder="Enter product name"
                    contextKey="search"
                    width={
                      Platform.OS === 'web'
                        ? undefined
                        : nativeHeaderSearchWidth
                    }
                    showLeadingIcon={true}
                    showBarcodeScan={true}
                    containerStyle={{
                      backgroundColor: '#fff',
                      borderColor: '#fff',
                      borderWidth: 0,
                    }}
                  />
                </View>
              ),
              tabBarLabelStyle: {
                fontSize: fs(12),
                fontFamily: GTFonts.regular,
                lineHeight: fs(18),
              },
              tabBarIcon: ({ focused }) => (
                <TabIconPill focused={focused}>
                  <MaterialIcons
                    name="format-list-bulleted"
                    size={iconSize(isTablet() ? 18 : focused ? 28 : 26)}
                    color={focused ? palette.blue[500] : theme.colors.textGray500}
                  />
                </TabIconPill>
              ),
              headerRight: () => (
                <View style={{ marginRight: s(3) }}>
                  <HeaderActions
                    actions={[
                      productsStatusAction,
                      {
                        type: 'menu',
                        items: [
                          {
                            title: 'Add Product',
                            leadingIcon: 'plus',
                            onPress: () =>
                              navigation.navigate('NewProduct' as never),
                            visible: isAdmin,
                          },
                          {
                            title: 'Product Settings',
                            leadingIcon: 'cog-outline',
                            onPress: () =>
                              navigation.navigate(
                                'ProductSettingsScreen' as never,
                              ),
                          },
                          {
                            title: 'Sort Products',
                            leadingIcon: 'sort-variant',
                            onPress: () => setFilterModal(true),
                          },
                        ],
                      },
                    ]}
                  />
                </View>
              ),
              headerLeft: props => (
                <HeaderLeft
                  {...props}
                  onPress={() => {
                    navigation.popTo('DrawerNavigation', { screen: 'Home' });
                  }}
                />
              ),
              headerStyle,
            }}>
            {(props: any) => <ProductsScreen {...props} />}
          </Tab.Screen>
        )}
        {!showservicedisplay && (
          <Tab.Screen
            name="Cart"
            options={({ navigation }) => ({
              headerShown: useNativeNav,
              headerStyle,
              headerTitleStyle: {
                color: 'white',
              },
              tabBarLabelStyle: {
                fontSize: fs(12),
                fontFamily: GTFonts.regular,
                lineHeight: fs(18),
              },
              headerTintColor: '#fff',
              tabBarIcon: ({ focused }) => (
                <TabIconPill focused={focused}>
                  <CartIcon
                    iconColor={
                      focused ? palette.blue[500] : theme.colors.textGray500
                    }
                    badgeBackgroundColor={palette.orange[500]}
                    badgeTextColor="#fff"
                    badgeBorderColor={palette.orange[500]}
                    size={iconSize(
                      isTablet()
                        ? iconSize(18)
                        : focused
                          ? iconSize(22)
                          : iconSize(20),
                    )}
                  />
                </TabIconPill>
              ),
              title: 'Basket',
              headerTitleAlign: 'center',
              headerTitle: () => (
                <View
                  style={{
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                  <Typography
                    style={{
                      color: 'white',
                      fontFamily: GTFonts.regular,
                      fontSize: fs(Platform.OS === 'web' ? 14 : 16),
                      lineHeight: fs(20),
                    }}>
                    Basket
                  </Typography>
                  <Typography
                    style={{
                      color: 'white',
                      fontFamily: GTFonts.regular,
                      fontSize: fs(12),
                      lineHeight: fs(16),
                      opacity: 0.95,
                    }}>
                    {moment(new Date()).format('MMM Do YYYY, h:mm:ss a')}
                  </Typography>
                </View>
              ),
              headerLeft: props => (
                <HeaderLeft
                  {...props}
                  onPress={() => {
                    navigation.navigate('MainScreen', {
                      screen: 'ProductsScreen',
                    });
                  }}
                />
              ),
              headerRight: () => (
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    marginRight: s(3),
                  }}>
                  {/* <TouchableOpacity
                  onPress={() => setCalcOpen(true)}
                  style={{
                    padding: s(4),
                  }}
                  hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                  <SimpleLineIcons
                    name="calculator"
                    size={iconSize(18)}
                    color={'#fff'}
                  />
                </TouchableOpacity> */}
                  <Menu
                    visible={menuVisibleCart}
                    onDismiss={closeMenuCart}
                    anchor={
                      <HeaderIconButton
                        tooltip="Open basket menu"
                        onPress={openMenuCart}
                        style={{ padding: s(4), borderRadius: s(20) }}
                        accessibilityLabel="Open menu">
                        <MaterialCommunityIcons
                          name="dots-vertical"
                          size={iconSize(isTablet() ? 26 : 28)}
                          color="#fff"
                        />
                      </HeaderIconButton>
                    }
                    anchorPosition="bottom">
                    {isAdmin ? (
                      <Menu.Item
                        leadingIcon="cog"
                        onPress={() => {
                          closeMenuCart();
                          navigation
                            .getParent()
                            ?.navigate('CartSettingsScreen' as never);
                        }}
                        title="Basket Settings"
                      />
                    ) : (
                      <Menu.Item
                        leadingIcon="cog"
                        onPress={() => {
                          closeMenuCart();
                          navigation
                            .getParent()
                            ?.navigate('SettingsScreen' as never);
                        }}
                        title="Settings"
                      />
                    )}
                  </Menu>
                </View>
              ),
            })}>
            {(props: any) => (
              <>
                <WebHeaderBarInjector
                  titleAlign="center"
                  title={
                    <View
                      style={{
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                      <Typography
                        style={{
                          color: 'white',
                          fontFamily: GTFonts.regular,
                          fontSize: fs(14),
                          lineHeight: fs(20),
                        }}>
                        Basket
                      </Typography>
                      <Typography
                        style={{
                          color: 'white',
                          fontFamily: GTFonts.regular,
                          fontSize: fs(12),
                          lineHeight: fs(16),
                          opacity: 0.95,
                        }}>
                        {moment(new Date()).format('MMM Do YYYY, h:mm:ss a')}
                      </Typography>
                    </View>
                  }
                  onBack={() =>
                    props.navigation.navigate('MainScreen', {
                      screen: 'ProductsScreen',
                    })
                  }
                  rightSlot={
                    <View
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                      <Menu
                        visible={menuVisibleCart}
                        onDismiss={closeMenuCart}
                        anchor={
                          <HeaderIconButton
                            tooltip="Open basket menu"
                            onPress={openMenuCart}
                            style={{ marginRight: s(4), padding: s(6) }}
                            accessibilityLabel="Open menu">
                            <MaterialCommunityIcons
                              name="dots-vertical"
                              size={iconSize(24)}
                              color="#fff"
                            />
                          </HeaderIconButton>
                        }
                        anchorPosition="bottom">
                        {isAdmin ? (
                          <Menu.Item
                            leadingIcon="cog"
                            onPress={() => {
                              closeMenuCart();
                              props.navigation
                                .getParent()
                                ?.navigate('CartSettingsScreen' as never);
                            }}
                            title="Basket Settings"
                          />
                        ) : (
                          <Menu.Item
                            leadingIcon="cog"
                            onPress={() => {
                              closeMenuCart();
                              props.navigation
                                .getParent()
                                ?.navigate('SettingsScreen' as never);
                            }}
                            title="Settings"
                          />
                        )}
                      </Menu>
                    </View>
                  }
                />
                <Cart {...props} />
              </>
            )}
          </Tab.Screen>
        )}
        {!showservicedisplay && (
          <Tab.Screen
            name="Orders"
            options={{
              headerShown: useNativeNav,
              headerStyle,
              headerTitleContainerStyle: { flex: 1 },
              headerTitleStyle: {
                color: 'white',
              },
              headerTintColor: '#fff',
              tabBarLabelStyle: {
                fontSize: fs(12),
                fontFamily: GTFonts.regular,
                lineHeight: fs(18),
              },
              tabBarIcon: ({ focused }) => (
                <TabIconPill focused={focused}>
                  <Ionicons
                    name="receipt-sharp"
                    size={iconSize(isTablet() ? 18 : focused ? 22 : 20)}
                    color={focused ? palette.blue[500] : theme.colors.textGray500}
                  />
                </TabIconPill>
              ),
              headerTitle: () => (
                <View
                  style={{
                    width: '100%',
                    maxWidth:
                      Platform.OS === 'web'
                        ? webHeaderSearchMaxWidth
                        : undefined,
                    // paddingHorizontal: s(4),
                  }}>
                  <AppSearchBar
                    placeholder="Customer name or Receipt id..."
                    width={
                      Platform.OS === 'web'
                        ? undefined
                        : nativeHeaderSearchWidth
                    }
                    value={orderSearchId}
                    onChangeText={setOrderSearchId}
                    onClear={handleClearSearch}
                    showLeadingIcon={true}
                    containerStyle={{
                      backgroundColor: '#fff',
                      borderColor: '#fff',
                      borderWidth: 0,
                    }}
                  />
                </View>
              ),
              headerLeft: props => (
                <HeaderLeft
                  {...props}
                  onPress={() => {
                    navigation.popTo('MainScreen', {
                      screen: 'ProductsScreen',
                    });
                  }}
                />
              ),
              headerRight: () => (
                <View style={{ marginRight: s(3) }}>
                  <HeaderActions
                    actions={[
                      {
                        type: 'icon',
                        icon: 'calendar-month-outline',
                        onPress: handleToggleOrdersDateFilter,
                        accessibilityLabel: 'Filter by date',
                      },
                      {
                        type: 'menu',
                        items: [
                          {
                            title: 'Refresh orders',
                            leadingIcon: 'reload',
                            onPress: () => refreshFunction?.(),
                          },
                          {
                            title: 'Order Settings',
                            leadingIcon: 'cog-outline',
                            onPress: () => {
                              try {
                                navigation.navigate(
                                  'OrderSettingsScreen' as never,
                                );
                              } catch (e) {
                                navigation
                                  .getParent()
                                  ?.navigate('OrderSettingsScreen' as never);
                              }
                            },
                            visible: isAdmin,
                          },
                        ],
                      },
                    ]}
                  />
                </View>
              ),
            }}>
            {(props: any) => (
              <>
                <WebHeaderBarInjector
                  title={
                    showTabletSidebarLayoutSetting && useSidebar
                      ? undefined
                      : <HeaderLocationPicker />
                  }
                  titleAlign={
                    showTabletSidebarLayoutSetting && useSidebar
                      ? undefined
                      : 'center'
                  }
                  onBack={() =>
                    navigation.popTo('MainScreen', {
                      screen: 'ProductsScreen',
                    })
                  }
                  rightSlot={
                    <HeaderActions
                      actions={[
                        {
                          type: 'icon',
                          icon: 'calendar-month-outline',
                          onPress: handleToggleOrdersDateFilter,
                          accessibilityLabel: 'Filter by date',
                        },
                        {
                          type: 'menu',
                          items: [
                            {
                              title: 'Refresh orders',
                              leadingIcon: 'reload',
                              onPress: () => refreshFunction?.(),
                            },
                            {
                              title: 'Order Settings',
                              leadingIcon: 'cog-outline',
                              onPress: () => {
                                try {
                                  navigation.navigate(
                                    'OrderSettingsScreen' as never,
                                  );
                                } catch (e) {
                                  navigation
                                    .getParent()
                                    ?.navigate('OrderSettingsScreen' as never);
                                }
                              },
                              visible: isAdmin,
                            },
                          ],
                        },
                      ]}
                    />
                  }>
                  <View
                    style={{
                      width: '100%',
                      maxWidth: webHeaderSearchMaxWidth,
                    }}>
                    <AppSearchBarWeb
                      placeholder="Customer name or Receipt id..."
                      value={orderSearchId}
                      onChangeText={setOrderSearchId}
                      onClear={handleClearSearch}
                      showLeadingIcon={true}
                      width={
                        showTabletSidebarLayoutSetting && useSidebar
                          ? width * 0.46
                          : width * 0.32
                      }
                      height={v(50)}
                      containerStyle={{
                        backgroundColor: '#fff',
                        borderColor: '#fff',
                        borderWidth: 0,
                      }}
                    />
                  </View>
                </WebHeaderBarInjector>
                <OrdersScreenTabNavigator
                  {...props}
                  dateFilterVisible={ordersDateFilterVisible}
                  dateFilterActive={ordersDateFilterActive}
                  dateFilterStartISO={ordersDateFilterStartISO}
                  dateFilterEndISO={ordersDateFilterEndISO}
                  onApplyDateFilter={handleApplyOrdersDateFilter}
                />
              </>
            )}
          </Tab.Screen>
        )}
        {isAdmin && (
          <Tab.Screen
            name="Office"
            options={{
              title: 'Office',
              tabBarLabel: 'Office',
              headerShown: false,
              tabBarLabelStyle: {
                fontSize: fs(12),
                fontFamily: GTFonts.regular,
                lineHeight: fs(18),
              },
              tabBarIcon: ({ focused }) => (
                <TabIconPill focused={focused}>
                  <Foundation
                    name="dollar"
                    size={iconSize(isTablet() ? 28 : focused ? 32 : 30)}
                    color={
                      focused ? palette.blue[500] : theme.colors.textGray500
                    }
                  />
                </TabIconPill>
              ),
            }}
            listeners={{
              tabPress: e => {
                e.preventDefault();
                void handleOfficeTabPress();
              },
            }}>
            {() => <View />}
          </Tab.Screen>
        )}
        <Tab.Screen
          name="Profile"
          options={{
            headerShown: useNativeNav,
            headerStyle,
            title: 'Profile',
            headerTitleStyle: {
              color: 'white',
              fontSize: fs(Platform.OS === 'web' ? 14 : 16),
              lineHeight: fs(20),
            },
            tabBarLabelStyle: {
              fontSize: fs(12),
              fontFamily: GTFonts.regular,
              lineHeight: fs(18),
            },
            headerTintColor: '#fff',
            // Bottom tab only: Office replaces Profile for admins.
            // Screen stays registered for employee Profile tab / deep links.
            ...(isAdmin
              ? {
                  tabBarButton: () => null,
                  tabBarItemStyle: { display: 'none' as const },
                }
              : {}),
            tabBarIcon: ({ focused }) => (
              <TabIconPill focused={focused}>
                <Ionicons
                  name="person"
                  size={iconSize(isTablet() ? 18 : focused ? 22 : 20)}
                  color={focused ? palette.blue[500] : theme.colors.textGray500}
                />
              </TabIconPill>
            ),
            headerLeft: props => (
              <HeaderLeft
                {...props}
                onPress={() => {
                  if (showservicedisplay) {
                    navigation.popTo('DrawerNavigation', { screen: 'Home' });
                  } else {
                    navigation.popTo('MainScreen', {
                      screen: 'ProductsScreen',
                    });
                  }
                }}
              />
            ),
            headerRight: () =>
              isOwner ? (
                <View style={{ marginRight: s(10) }}>
                  <HeaderInternetStatusIcon />
                </View>
              ) : null,
          }}>
          {(props: any) => (
            <>
              <WebHeaderBarInjector
                title="Profile"
                rightSlot={
                  isOwner ? (
                    <View style={{ marginRight: s(10) }}>
                      <HeaderInternetStatusIcon />
                    </View>
                  ) : undefined
                }
                onBack={() => {
                  if (showservicedisplay) {
                    navigation.popTo('DrawerNavigation', { screen: 'Home' });
                  } else {
                    navigation.popTo('MainScreen', {
                      screen: 'ProductsScreen',
                    });
                  }
                }}
              />
              <EmployeeProfileScreen {...props} />
            </>
          )}
        </Tab.Screen>
        </Tab.Navigator>
      </WebSidebarLayout>
      <CalculatorModal
        visible={calcOpen}
        onDismiss={() => setCalcOpen(false)}
        storageKey="cart-calculator:v1"
        title="Cart Calculator"
      />
    </>
  );
};

const styles = StyleSheet.create({
  cartIconContainer: {},

  // Enhanced icon container styles for subtle visual feedback
  iconContainer: {
    padding: s(2),
    borderRadius: s(8),
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: s(40),
    minHeight: v(32),
  },

  activeIconContainer: {
    padding: s(2),
    borderRadius: s(8),
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 40,
    minHeight: 32,
    // transform: [{ scale: 1.05 }],
    // Subtle orange border accent to complement blue brand
    borderWidth: borderWidth(0.5),
    borderColor: '#FF6B3520', // Very subtle orange accent
  },

  tabIconWrap: {
    alignItems: 'center',
    justifyContent: 'center',
  },

  tabIconPill: {
    position: 'absolute',
    width: s(48),
    height: v(30),
    borderRadius: v(15),
    backgroundColor: palette.blue[50], // #E8F2FF soft brand tint
  },

  // Keep existing styles that might be used elsewhere (for backwards compatibility)
  icons: {
    width: '35%',
    justifyContent: 'center',
    alignItems: 'center',
    height: v(29),
    alignSelf: 'center',
    marginLeft: 18,
    borderWidth: borderWidth(0.5),
    borderColor: '#1560bd',
    borderRadius: s(5),
    backgroundColor: '#fff',
  },
  iconspressin: {
    width: '35%',
    justifyContent: 'center',
    alignItems: 'center',
    height: v(29),
    alignSelf: 'center',
    marginLeft: 18,
    borderWidth: borderWidth(Platform.OS === 'web' ? 0.5 : 1),
    borderColor: '#fff',
    color: '#fff',
    borderRadius: s(5),
  },
});

export default MainScreenTabNavigator;
